import { useState, useEffect, useCallback } from 'react';
import { Globe, AlertTriangle, Calendar, ShieldAlert, Plus, X } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface GlobalProfile {
  id: string;
  employee_id: string;
  employee_name: string;
  country_code: string;
  country_name: string;
  region?: string;
  work_visa_type?: string;
  work_authorization_expiry?: string;
  local_tax_id?: string;
  statutory_leave_allowance?: number;
  local_compliance_status: 'compliant' | 'attention_required' | 'non_compliant';
  compliance_items: Array<{ item: string; status: 'ok' | 'pending' | 'overdue'; due_date?: string }>;
  classification_risk: 'low' | 'medium' | 'high';
  classification_flags: string[];
}

interface CountryBreakdown {
  code: string;
  name: string;
  count: number;
  compliance_alerts: number;
}

interface ComplianceEvent {
  date: string;
  type: 'filing' | 'holiday' | 'work_auth_expiry';
  title: string;
  country_code: string;
  country_name: string;
  urgency: 'normal' | 'warning' | 'critical';
}

interface ClassificationResult {
  total_contractors: number;
  flagged: Array<{
    employee_id: string;
    employee_name: string;
    email: string;
    department?: string;
    classification_risk: 'low' | 'medium' | 'high';
    flags: string[];
  }>;
  high_risk: number;
  medium_risk: number;
}

// ─── World Map SVG (simplified dot map) ──────────────────────

const COUNTRY_COORDS: Record<string, [number, number]> = {
  US: [190, 130],
  CA: [175, 100],
  GB: [430, 90],
  DE: [455, 95],
  FR: [447, 103],
  IN: [540, 140],
  AU: [580, 200],
};

function WorldMapDots({ countries }: { countries: CountryBreakdown[] }) {
  const activeCodes = new Set(countries.map(c => c.code));
  return (
    <div className="relative">
      <svg viewBox="0 0 760 380" className="w-full opacity-90">
        {/* Simplified world outline - just background */}
        <rect width="760" height="380" fill="transparent" />
        {/* Grid lines */}
        {[0, 95, 190, 285, 380].map(y => (
          <line key={y} x1="0" y1={y} x2="760" y2={y} stroke="#27272a" strokeWidth="0.5" />
        ))}
        {[0, 152, 304, 456, 608, 760].map(x => (
          <line key={x} x1={x} y1="0" x2={x} y2="380" stroke="#27272a" strokeWidth="0.5" />
        ))}
        {/* Country dots */}
        {Object.entries(COUNTRY_COORDS).map(([code, [x, y]]) => {
          const country = countries.find(c => c.code === code);
          const isActive = activeCodes.has(code);
          const hasAlert = country ? country.compliance_alerts > 0 : false;
          return (
            <g key={code}>
              <circle
                cx={x} cy={y} r={isActive ? 7 : 4}
                fill={isActive ? (hasAlert ? '#ef4444' : '#f59e0b') : '#3f3f46'}
                opacity={isActive ? 1 : 0.4}
              />
              {isActive && (
                <>
                  <text x={x} y={y + 18} textAnchor="middle" fontSize="9" fill="#a1a1aa">{code}</text>
                  {country && (
                    <text x={x} y={y + 27} textAnchor="middle" fontSize="8" fill="#f59e0b">{country.count}</text>
                  )}
                </>
              )}
            </g>
          );
        })}
      </svg>
      <p className="text-xs text-muted-foreground text-center mt-1">Amber = employees present · Red = compliance alert</p>
    </div>
  );
}

// ─── Add Profile Modal ────────────────────────────────────────

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IN', name: 'India' },
];

interface AddProfileModalProps {
  onClose: () => void;
  onSaved: () => void;
}

function AddProfileModal({ onClose, onSaved }: AddProfileModalProps) {
  const [form, setForm] = useState({
    employee_id: '',
    country_code: 'US',
    work_visa_type: '',
    work_authorization_expiry: '',
    local_tax_id: '',
    statutory_leave_allowance: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!form.employee_id.trim()) { setError('Employee ID is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/people/global/profiles/${encodeURIComponent(form.employee_id.trim())}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          country_code: form.country_code,
          work_visa_type: form.work_visa_type || undefined,
          work_authorization_expiry: form.work_authorization_expiry || undefined,
          local_tax_id: form.local_tax_id || undefined,
          statutory_leave_allowance: form.statutory_leave_allowance ? parseInt(form.statutory_leave_allowance) : undefined,
        }),
      });
      if (!res.ok) throw new Error('Failed to save global profile');
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Add Global Profile</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Employee ID</label>
            <input value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" placeholder="Employee ID..." />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Country</label>
            <select value={form.country_code} onChange={e => setForm(f => ({ ...f, country_code: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Visa Type</label>
              <input value={form.work_visa_type} onChange={e => setForm(f => ({ ...f, work_visa_type: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" placeholder="H-1B, TN, etc." />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Work Auth Expiry</label>
              <input type="date" value={form.work_authorization_expiry} onChange={e => setForm(f => ({ ...f, work_authorization_expiry: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Local Tax ID</label>
              <input value={form.local_tax_id} onChange={e => setForm(f => ({ ...f, local_tax_id: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Leave Days/Year</label>
              <input type="number" value={form.statutory_leave_allowance} onChange={e => setForm(f => ({ ...f, statutory_leave_allowance: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" placeholder="25" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          <button onClick={() => void save()} disabled={saving} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

type Tab = 'overview' | 'calendar' | 'classification';

export function GlobalWorkforcePage() {
  const [profiles, setProfiles] = useState<GlobalProfile[]>([]);
  const [countryBreakdown, setCountryBreakdown] = useState<CountryBreakdown[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<ComplianceEvent[]>([]);
  const [classification, setClassification] = useState<ClassificationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('overview');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [profilesRes, calendarRes, classificationRes] = await Promise.all([
        fetch('/api/people/global/profiles'),
        fetch('/api/people/global/compliance-calendar'),
        fetch('/api/people/global/classification'),
      ]);
      if (profilesRes.ok) {
        const data = await profilesRes.json() as { profiles: GlobalProfile[]; country_breakdown: CountryBreakdown[] };
        setProfiles(data.profiles);
        setCountryBreakdown(data.country_breakdown);
      }
      if (calendarRes.ok) {
        const data = await calendarRes.json() as { events: ComplianceEvent[] };
        setCalendarEvents(data.events);
      }
      if (classificationRes.ok) {
        setClassification(await classificationRes.json() as ClassificationResult);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const totalAlerts = countryBreakdown.reduce((a, c) => a + c.compliance_alerts, 0);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Globe className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">Global Workforce</h1>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm"
        >
          <Plus className="w-3.5 h-3.5" /> Add Profile
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Total Global Employees</p>
          <p className="text-2xl font-bold text-foreground">{profiles.length}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Countries</p>
          <p className="text-2xl font-bold text-foreground">{countryBreakdown.length}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Compliance Alerts</p>
          <p className={`text-2xl font-bold ${totalAlerts > 0 ? 'text-red-400' : 'text-green-400'}`}>{totalAlerts}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1">
        {(['overview', 'calendar', 'classification'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${tab === t ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'}`}>
            {t === 'calendar' ? 'Compliance Calendar' : t === 'classification' ? 'Classification Risk' : 'Overview'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : tab === 'overview' ? (
        <div className="space-y-6">
          {/* World map */}
          <div className="glass rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Employee Locations</h3>
            <WorldMapDots countries={countryBreakdown} />
          </div>

          {/* Country breakdown table */}
          <div className="glass rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Country Breakdown</h3>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-5 py-2.5 font-medium text-muted-foreground">Country</th>
                  <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Employees</th>
                  <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Compliance Alerts</th>
                </tr>
              </thead>
              <tbody>
                {countryBreakdown.length === 0 ? (
                  <tr><td colSpan={3} className="text-center px-5 py-6 text-muted-foreground">No global profiles yet</td></tr>
                ) : countryBreakdown.map(c => (
                  <tr key={c.code} className="border-b border-border/50 hover:bg-surface-3/50">
                    <td className="px-5 py-2.5">
                      <span className="font-medium text-foreground">{c.name}</span>
                      <span className="text-muted-foreground ml-1.5">({c.code})</span>
                    </td>
                    <td className="text-center px-4 py-2.5 text-foreground">{c.count}</td>
                    <td className="text-center px-4 py-2.5">
                      {c.compliance_alerts > 0 ? (
                        <span className="flex items-center justify-center gap-1 text-red-400">
                          <AlertTriangle className="w-3 h-3" /> {c.compliance_alerts}
                        </span>
                      ) : (
                        <span className="text-green-400">None</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : tab === 'calendar' ? (
        <div className="space-y-3">
          {calendarEvents.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center">
              <Calendar className="w-10 h-10 text-amber-500/20 mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">No compliance events in the next 90 days</p>
            </div>
          ) : calendarEvents.map((event, i) => (
            <div key={i} className={`glass rounded-xl p-4 border-l-2 ${event.urgency === 'critical' ? 'border-red-500' : event.urgency === 'warning' ? 'border-amber-500' : 'border-border'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{event.title}</p>
                  <p className="text-xs text-muted-foreground">{event.country_name} · {event.type.replace(/_/g, ' ')}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold text-foreground">{new Date(event.date).toLocaleDateString()}</p>
                  {event.urgency !== 'normal' && (
                    <span className={`text-xs ${event.urgency === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                      {event.urgency === 'critical' ? 'Critical' : 'Warning'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Classification Risk */
        classification && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="glass rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">Total Contractors</p>
                <p className="text-2xl font-bold text-foreground">{classification.total_contractors}</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">High Risk</p>
                <p className="text-2xl font-bold text-red-400">{classification.high_risk}</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">Medium Risk</p>
                <p className="text-2xl font-bold text-amber-400">{classification.medium_risk}</p>
              </div>
            </div>

            {classification.flagged.length === 0 ? (
              <div className="glass rounded-xl p-8 text-center">
                <ShieldAlert className="w-10 h-10 text-green-500/20 mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">No misclassification risks detected</p>
              </div>
            ) : (
              <div className="space-y-3">
                {classification.flagged.map(f => (
                  <div key={f.employee_id} className={`glass rounded-xl p-4 border-l-2 ${f.classification_risk === 'high' ? 'border-red-500' : 'border-amber-500'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{f.employee_name}</p>
                        <p className="text-xs text-muted-foreground">{f.email} · {f.department ?? 'No department'}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${f.classification_risk === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {f.classification_risk} risk
                      </span>
                    </div>
                    <ul className="mt-2 space-y-0.5">
                      {f.flags.map((flag, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" /> {flag}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      )}

      {showAdd && <AddProfileModal onClose={() => setShowAdd(false)} onSaved={() => void load()} />}
    </div>
  );
}
